import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from "react";
import * as RxMenu from "@radix-ui/react-dropdown-menu";
import { Check } from "lucide-react";
import { cn } from "@/lib/cn";

const Root = RxMenu.Root;
const Trigger = RxMenu.Trigger;

const Content = forwardRef<
  ElementRef<typeof RxMenu.Content>,
  ComponentPropsWithoutRef<typeof RxMenu.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <RxMenu.Portal>
    <RxMenu.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 min-w-[10rem] overflow-hidden rounded-[var(--radius-md)] border border-border-subtle bg-surface p-1 shadow-[var(--shadow-3)]",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        className,
      )}
      {...props}
    />
  </RxMenu.Portal>
));
Content.displayName = "Menu.Content";

const Item = forwardRef<
  ElementRef<typeof RxMenu.Item>,
  ComponentPropsWithoutRef<typeof RxMenu.Item> & { destructive?: boolean }
>(({ className, destructive, ...props }, ref) => (
  <RxMenu.Item
    ref={ref}
    className={cn(
      "relative flex cursor-pointer select-none items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-sm outline-none",
      "text-fg",
      "data-[highlighted]:bg-surface-hover data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      "[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-fg-subtle",
      destructive && "text-danger-fg data-[highlighted]:bg-danger-bg [&_svg]:text-danger-fg",
      className,
    )}
    {...props}
  />
));
Item.displayName = "Menu.Item";

const CheckboxItem = forwardRef<
  ElementRef<typeof RxMenu.CheckboxItem>,
  ComponentPropsWithoutRef<typeof RxMenu.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <RxMenu.CheckboxItem
    ref={ref}
    checked={checked}
    className={cn(
      "relative flex cursor-pointer select-none items-center gap-2 rounded-[var(--radius-sm)] py-1.5 pl-7 pr-2 text-sm outline-none",
      "data-[highlighted]:bg-surface-hover data-[disabled]:opacity-50",
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex size-3.5 items-center justify-center">
      <RxMenu.ItemIndicator>
        <Check className="size-3.5 text-accent-text" />
      </RxMenu.ItemIndicator>
    </span>
    {children}
  </RxMenu.CheckboxItem>
));
CheckboxItem.displayName = "Menu.CheckboxItem";

const Separator = forwardRef<
  ElementRef<typeof RxMenu.Separator>,
  ComponentPropsWithoutRef<typeof RxMenu.Separator>
>(({ className, ...props }, ref) => (
  <RxMenu.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-border-subtle", className)}
    {...props}
  />
));
Separator.displayName = "Menu.Separator";

const Label = forwardRef<
  ElementRef<typeof RxMenu.Label>,
  ComponentPropsWithoutRef<typeof RxMenu.Label>
>(({ className, ...props }, ref) => (
  <RxMenu.Label
    ref={ref}
    className={cn(
      "px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-fg-subtle",
      className,
    )}
    {...props}
  />
));
Label.displayName = "Menu.Label";

export const Menu = {
  Root,
  Trigger,
  Content,
  Item,
  CheckboxItem,
  Separator,
  Label,
};
